import max from 'lodash/max';
import get from 'lodash/get';
import type { PayloadError } from 'relay-runtime';
import { VALIDATION_ERROR_NAME } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import {
	isClientFetchError,
	isHttpClientErrorStatusCode,
} from '@atlassian/jira-fetch/src/utils/is-error.tsx';

/**
 * We capture all client side errors as abortable errors.
 * We don't count these in our SLOs and have separate detectors!
 * Reference: https://hello.atlassian.net/wiki/spaces/Spork/pages/5706276610/DACI+Include+or+exclude+4XX+errors+in+SLOs+for+Jira
 * Since isClientFetchError doesn't check for ValidationError, we need to check for it here.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isAbortableError = (error: any) => {
	const statusCode = extractStatusCode(error);
	if (
		isHttpClientErrorStatusCode(statusCode) ||
		(!statusCode && isClientFetchError(error)) ||
		error?.name === VALIDATION_ERROR_NAME
	) {
		return true;
	}
	return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PartialError = Partial<Error> & Record<PropertyKey, any>;
type BackendError = Readonly<GraphqlError | PayloadError>;
type GraphqlError = {
	message: string;
	path: string[];
	extensions: {
		statusCode: number | null;
		userMessage?: string;
		errorType?: string;
	};
};

const getStatusCodeFromGraphQLErrors = (
	graphQLErrors: ReadonlyArray<BackendError>,
): number | null => {
	const statusCodes = graphQLErrors
		.map((e) => get(e, ['extensions', 'statusCode']))
		.filter((statusCode) => !!statusCode);
	const statusCode = max(statusCodes);
	return statusCode || null;
};

const extractStatusCode = (error: PartialError): number | null => {
	const errorStatusCode = get(error, ['statusCode'], null);
	const graphQLErrors = get(error, ['graphQLErrors'], null);
	const networkError = get(error, ['networkError'], null);
	const statusCode = get(networkError, ['statusCode'], null);

	if (typeof errorStatusCode === 'number') {
		return errorStatusCode;
	}

	if (typeof statusCode === 'number') {
		return statusCode;
	}

	if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
		return getStatusCodeFromGraphQLErrors(graphQLErrors);
	}

	return null;
};
